import { Component, Inject } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { StammkundeService } from '../../../Services/stammkunde.service';
import {
  Auftrag,
  AuftragCreate,
  AUFTRAG_STATUS_LABELS,
  AUFTRAG_STATUS_ORDER,
  AuftragStatus,
  WarehouseRef
} from '../../../Models/stammkunde.model';

export interface AuftragFormData {
  customerId: number;
  auftrag?: Auftrag;
  linkedWarehouses: WarehouseRef[];
}

/** Dialog zum Anlegen/Bearbeiten eines Stammkunden-Auftrags. */
@Component({
  selector: 'app-auftrag-form',
  templateUrl: './auftrag-form.component.html',
  styleUrls: ['./auftrag-form.component.css', '../stammkunden-theme.css']
})
export class AuftragFormComponent {
  isEdit: boolean;
  saving = false;

  title = '';
  description = '';
  orderNumber = '';
  location = '';
  receivedDate: string | null = null;
  startDate: string | null = null;
  dueDate: string | null = null;
  primaryWarehouseId: number | null = null;
  status: AuftragStatus = 'New';

  readonly statusOptions = AUFTRAG_STATUS_ORDER;
  readonly statusLabels = AUFTRAG_STATUS_LABELS;

  constructor(
    public dialogRef: MatDialogRef<AuftragFormComponent>,
    @Inject(MAT_DIALOG_DATA) public data: AuftragFormData,
    private stammkundeService: StammkundeService,
    private snackBar: MatSnackBar
  ) {
    this.isEdit = !!data.auftrag;
    const a = data.auftrag;
    if (a) {
      this.title = a.title;
      this.description = a.description ?? '';
      this.orderNumber = a.orderNumber ?? '';
      this.location = a.location ?? '';
      this.receivedDate = this.toDateInput(a.receivedDate);
      this.startDate = this.toDateInput(a.startDate);
      this.dueDate = this.toDateInput(a.dueDate);
      this.primaryWarehouseId = a.primaryWarehouseId ?? null;
      this.status = a.status;
    }
  }

  save(): void {
    if (!this.title.trim()) return;
    this.saving = true;

    const payload: AuftragCreate = {
      customerId: this.data.customerId,
      title: this.title.trim(),
      description: this.description.trim() || undefined,
      orderNumber: this.orderNumber.trim() || undefined,
      location: this.location.trim() || undefined,
      receivedDate: this.receivedDate || null,
      startDate: this.startDate || null,
      dueDate: this.dueDate || null,
      primaryWarehouseId: this.primaryWarehouseId,
      status: this.isEdit ? this.status : undefined
    };

    const request$ = this.isEdit
      ? this.stammkundeService.updateAuftrag(this.data.auftrag!.id, payload)
      : this.stammkundeService.createAuftrag(payload);

    request$.subscribe({
      next: result => {
        this.saving = false;
        this.dialogRef.close(result);
      },
      error: err => {
        this.saving = false;
        const message = err?.error?.message || 'Fehler beim Speichern des Auftrags.';
        this.snackBar.open(message, 'OK', { duration: 5000 });
      }
    });
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  private toDateInput(value?: string | null): string | null {
    if (!value) return null;
    // Lokale Datumskomponenten verwenden – toISOString() würde in Zeitzonen != UTC
    // den Tag verschieben (Mitternacht lokal -> Vortag in UTC).
    const d = new Date(value);
    const y = d.getFullYear();
    const m = `${d.getMonth() + 1}`.padStart(2, '0');
    const day = `${d.getDate()}`.padStart(2, '0');
    return `${y}-${m}-${day}`;
  }
}
